import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BackArrowIcon from "../../../components/Icons/BackArrowIcon";
import SearchSelectInput from "../../../components/comman/SearchSelectInput";
import PlusIcon from "../../../components/Icons/PlusIcon";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { callApi } from "../../../ApiHelper/ApiHelper";
import BgBlurLoader from "../../../components/Loaders/BgBlurLoader";
import BudgetRequestSection from "./BudgetRequestSection";
import moment from "moment";
import calculateFinancialYears, { parseAmount } from "../../../utility/CommonFunctions";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import UploadPlusIcon from "../../../components/Icons/UploadPlusIcon";
import DeleteIcon from "../../../components/Icons/DeleteIcon";
import ViewDocumentModal from "../ViewDocumentModal";


export default function CreateBudgetRequest() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [siteList, setSiteList] = useState([]);
    const [isLoadingSite, setIsLoadingSite] = useState(false);
    const [siteName, setSiteName] = useState("");
    const [financialYears, setFinancialYears] = useState([]);
    const [activeFinancialYear, setActiveFinancialYear] = useState("");
    const [expenseHeadType, setExpenseHeadType] = useState([]);
    const [budgetDetails, setBudgetDetails] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [modal, setModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const toggleModal = () => {
        setModal(!modal);
    };

    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        shouldFocusError: false,  // Prevent focus errors with disabled/readonly fields
        defaultValues: {
            siteId: "",
            requestType: "1",
            sections: [{ requestType: "", expenseHeads: [{ expenseHeadId: "", siteBudgetId: "", expectedAmount: "", items: [] }] }],
            description: "",
            activeFinancialYear: "",
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "sections",
    });

    const siteId = watch("siteId");
    const requestType = watch("requestType");

    const getAssignedSites = (inputValue = null) => {
        setIsLoadingSite(true);
        const url = `${apiUrl?.get_user_site}${inputValue ? `?search=${inputValue}` : ""
            }`;
        callApi(url, { method: "GET" }).then((res) => {
            setIsLoadingSite(false);
            if (res?.status) {
                setSiteList(
                    res?.data?.site?.map((item) => ({
                        ...item,
                        label: item?.site?.name,
                        value: item?.site?.id,
                    })) || []
                );
            } else {
                toast.error(res?.message);
            }
        });
    };


    useEffect(() => {
        if (activeFinancialYear && siteId) {
            getExpenseHeadType();
        }
    }, [activeFinancialYear, siteId]);


    useEffect(() => {
        getAssignedSites();
        if (location?.state?.type === "create") {
            setValue("siteId", location?.state?.siteId);
            setSiteName(location?.state?.siteName);
            handleFinancialYearCalculation(location?.state?.data);
        } else if (location?.state?.type === "edit") {
            setValue("siteId", location?.state?.siteId);
            setSiteName(location?.state?.siteName);
            handleFinancialYearCalculation(location?.state?.site);
        }
        else {
            if (location?.state) {
                const GetBudgetRequestDetail = () => {
                    let options = {
                        method: "GET",
                    };
                    let API = apiUrl?.get_site_budget_request_detail + `budgetRequestId=${location?.state?.data?.id}`;
                    callApi(API, options).then((response) => {
                        if (response?.status === true) {
                            const data = response?.data;

                            // Set site information
                            if (data?.site) {
                                setValue("siteId", data?.site?.id);
                                setValue("description", data?.description);
                                setSiteName(data?.site?.name);
                                handleFinancialYearCalculation(data?.site);
                            }
                            setBudgetDetails(data);
                        }
                    });
                };
                GetBudgetRequestDetail();
            }
            handleFinancialYearCalculation(null);
        }
    }, [location?.state]);

    useEffect(() => {
        // console.log("budgetDetails", budgetDetails);
        if (budgetDetails?.BudgetRequestHeads?.length > 0) {
            const groupedByType = {};
            budgetDetails?.BudgetRequestHeads?.forEach(head => {
                const type = head?.type;
                if (!groupedByType[type]) {
                    groupedByType[type] = [];
                }
                groupedByType[type].push(head);
            });

            // Transform grouped data to sections format
            const sections = Object.entries(groupedByType).map(([type, heads]) => {
                // console.log("type", type);
                let requestType = type;
                if (type === "supplyBudget") requestType = "Supply";
                else if (type === "serviceBudget") requestType = "Service";
                else if (type === "staffBudget") requestType = "Staff";
                else if (type === "generalBudget") requestType = "General";
                // staffBudget remains the same

                const expenseHeads = heads.map(head => {
                    const items = head?.budgetrequestitems?.map(item => ({
                        itemId: item?.itemId || item?.designationId,
                        siteBudgetItemId: item?.siteBudgetItemId,
                        hsnCode: item?.item?.hsnCode,
                        quantity: item?.quantity || "",
                        rate: item?.rate || "",
                        gst: item?.gst || "0",
                        month: item?.month || "",
                        amount: item?.amount || "",
                        originalMonth: item?.month,
                        originalAmount: item?.amount,
                        originalRate: item?.rate,
                        originalGst: item?.gst,
                        availableQty: (item?.freezedQuantity || 0)
                    })) || [];

                    return {
                        expenseHeadId: head?.expenseHeadId || head?.departmentId,
                        siteBudgetId: head?.siteBudgetId,
                        expectedAmount: head?.expectedAmount || "",
                        description: head?.description || "",
                        items: items
                    };
                });

                return {
                    requestType: requestType,
                    expenseHeads: expenseHeads
                };
            });
            setExistingFiles(budgetDetails?.documents)
            // Set sections data
            setValue("sections", sections);
        }
    }, [expenseHeadType, budgetDetails,]);

    const getExpenseHeadType = () => {
        callApi(apiUrl.get_expense_head_type + `?from=budgetRequest&financialYear=${activeFinancialYear}&siteId=${siteId}`).then((res) => {
            if (res.status) {
                setExpenseHeadType(res.data);
            } else {
                toast.error(res.message);
            }
        });
    };

    const handleFinancialYearCalculation = (data) => {
        const startDate = data?.site?.startDate || data?.startDate;
        const endDate = data?.site?.endDate || data?.endDate;

        let fyStartYear;
        const now = moment();
        const month = now.month() + 1;
        const year = now.year();

        if (!startDate || !endDate) {
            fyStartYear = month >= 4 ? year : year - 1;
            const currentFY = `${fyStartYear}-${fyStartYear + 1}`;
            setFinancialYears([currentFY]);
            setActiveFinancialYear(currentFY);
            setValue("activeFinancialYear", currentFY);
            return;
        }

        const fys = calculateFinancialYears(
            moment.utc(startDate).format("YYYY-MM-DD"),
            moment.utc(endDate).format("YYYY-MM-DD")
        );

        setFinancialYears(fys);

        fyStartYear = month >= 4 ? year : year - 1;
        const currentFY = `${fyStartYear}-${fyStartYear + 1}`;

        const selectedFY = fys.includes(currentFY) ? currentFY : fys[0] || "";
        setActiveFinancialYear(selectedFY);
        setValue("activeFinancialYear", selectedFY);
    };

    // File upload handlers
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            const newFiles = files.map(file => ({
                file: file,
                name: file.name,
                type: file.type,
                key: `file_${Date.now()}_${Math.random()}`,
            }));
            setUploadedFiles(prev => [...prev, ...newFiles]);
            // Reset input to allow re-selecting files
            e.target.value = '';
        }
    };

    const handleRemoveFile = (fileKey) => {
        setUploadedFiles(prev => prev.filter(f => f.key !== fileKey));
    };


    const onSubmit = (data) => {
        if (!data?.sections || data?.sections?.length === 0) {
            toast.error("Please add at least one section.");
            return;
        }

        const payload = {
            siteId: data?.siteId,
            ...(location?.state?.type === "updates" ? { budgetRequestId: location?.state?.data?.id } : {}),
            financialYear: data?.activeFinancialYear,
            ...(location?.state?.type !== "updates" ? { type: location?.state?.type === "edit" && !location?.state?.isDelete ? 2 : 1 } : {}),
            ...(location?.state?.isDelete ? { type: 3 } : {}),
            description: data?.description,
            budgetRequestHead: data?.sections?.flatMap(section => {
                let mappedType = section?.requestType;
                if (section?.requestType === "Supply") mappedType = "supplyBudget";
                if (section?.requestType === "Staff") mappedType = "staffBudget";
                else if (section?.requestType === "Service") mappedType = "serviceBudget";
                else if (section?.requestType === "General") mappedType = "generalBudget";

                return section?.expenseHeads?.map(head => {
                    const reductionAmount = parseAmount(head?.expectedAmount);
                    const currentTotal = parseAmount(head?.currentBudget);
                    const isFullDelete = location?.state?.isDelete && (
                        head?.willDeleteExpenseHead ||
                        !reductionAmount ||
                        (currentTotal > 0 && reductionAmount >= currentTotal)
                    );

                    return {
                        ...(section?.requestType === "Staff" ? { departmentId: head?.expenseHeadId } : { expenseHeadId: head?.expenseHeadId }),
                        ...(isFullDelete ? { willDeleteExpenseHead: true } : {}),
                        expectedAmount: section?.requestType === "Staff" ? head?.items?.reduce((total, item) => total + parseAmount(item?.amount), 0) : parseAmount(head?.expectedAmount),
                        description: head?.description,
                        requestType: mappedType,
                        ...(location?.state?.type === "edit" || location?.state?.isDelete ? { siteBudgetId: head?.siteBudgetId } : {}),
                        ...(section?.requestType === "Staff" ? {
                            budgetRequestItem: head?.items?.map(item => ({
                                designationId: item?.itemId,
                                ...(location?.state?.type === "edit" || location?.state?.isDelete ? { siteBudgetItemId: item?.siteBudgetItemId } : {}),
                                quantity: item?.quantity,
                                rate: item?.rate,
                                month: item?.month,
                                amount: parseAmount(item?.amount),
                            }))
                        } : {})
                    }
                })
            })
        };


        const formData = new FormData();

        for (const key in payload) {
            if (payload[key] !== undefined && payload[key] !== null) {
                if (key === "budgetRequestHead") {
                    formData.append(key, JSON.stringify(payload[key]));
                } else {
                    formData.append(key, payload[key]);
                }
            }
        }

        uploadedFiles.forEach((f) => formData.append(f.name, f.file));


        if (payload?.budgetRequestHead?.length === 0) {
            toast.error("Please add valid expense heads and items.");
            return;
        }

        // console.log("Payload:", payload);
        // console.log("data:", data?.sections);

        setIsLoading(true);
        callApi(location?.state?.type === "updates" ? apiUrl.update_site_budget_request : apiUrl.create_site_budget_request, {
            method: "POST",
            form_data: formData
        }).then(res => {
            setIsLoading(false);
            if (res?.status) {
                toast.success(res?.message);
                navigate(-1);
            } else {
                toast.error(res?.message);
            }
        });
    };

    const handleDeleteDocument = (docId) => {
        const API = `${apiUrl.delete_document}?documentId=${docId}`;
        callApi(API, { method: "GET" }).then((response) => {
            if (response?.status === true) {
                setExistingFiles((prev) => prev.filter((doc) => doc.id !== docId));
                // toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        });
    };

    return (
        <>
            {isLoading && <BgBlurLoader />}
            <div className="content container-fluid">
                <div className="content-head mb-4 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="btn back-arrow"
                        >
                            <BackArrowIcon />
                        </button>
                        <h1 className="mb-0">{location?.state?.type === "updates" ? "Edit" : "Create"} Budget Request</h1>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-12 col-md-6">
                                    <div className="form-group mb-3">
                                        <Controller
                                            name="siteId"
                                            control={control}
                                            rules={{ required: "Site is required" }}
                                            render={({ field, fieldState }) => (
                                                <SearchSelectInput
                                                    label="Site"
                                                    placeholder="Search Site"
                                                    value={siteName}
                                                    onSelect={(option) => {
                                                        field.onChange(option?.value);
                                                        setSiteName(option?.label);
                                                        const site = siteList?.find((s) => s?.value === option?.value);
                                                        setValue("sections", [{ requestType: "", expenseHeads: [{ expenseHeadId: "", siteBudgetId: "", expectedAmount: "", items: [] }] }]);
                                                        handleFinancialYearCalculation(site);
                                                    }}
                                                    onBlur={field.onBlur}
                                                    errors={fieldState.error?.message}
                                                    options={siteList}
                                                    onSearch={getAssignedSites}
                                                    isLoading={isLoadingSite}
                                                    required={true}
                                                    disabled={location?.state?.isDisabled ? location?.state?.isDisabled : false}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                                {<div className="col-12 col-md-6">
                                    <div className="form-group mb-3">
                                        <label className="form-label">Financial Year</label>
                                        <Controller
                                            name="activeFinancialYear"
                                            control={control}
                                            render={({ field }) => (
                                                <select
                                                    className="form-select"
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e.target.value);
                                                        setActiveFinancialYear(e.target.value);
                                                    }}
                                                    disabled={!siteId ? true : location?.state?.isDisabled ? location?.state?.isDisabled : false}
                                                >
                                                    {financialYears?.map((fy) => (
                                                        <option key={fy} value={fy}>
                                                            {fy}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        />
                                    </div>
                                </div>}
                            </div>
                        </div>
                    </div>

                    {fields.map((field, index) => (
                        <BudgetRequestSection
                            key={field.id}
                            control={control}
                            register={register}
                            sectionIndex={index}
                            removeSection={remove}
                            siteId={siteId}
                            errors={errors}
                            setValue={setValue}
                            usedRequestTypes={watch("sections")?.map((s) => s.requestType) || []}
                            activeFinancialYear={activeFinancialYear}
                            isEditMode={location?.state?.type === "edit" || location?.state?.type === "updates"}
                            expenseHeadType={expenseHeadType}
                        />
                    ))}

                    <div className="d-flex justify-content-end mb-4">
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => append({ requestType: "", expenseHeads: [{ expenseHeadId: "", expectedAmount: "", items: [] }] })}
                        >
                            <PlusIcon /> Add Section
                        </button>
                    </div>

                    {/* <div className="card mb-4">
                        <div className="card-body">
                            <div className="form-group">
                                <label className="form-label">Description</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Type here..."
                                    {...register("description")}
                                ></textarea>
                            </div>
                        </div>
                    </div> */}
                    <div className="col-12 ">
                        <div className="form-group mb-3">
                            <label htmlFor="totalLeavePerMonth" className="form-label">
                                Description
                                {/* <span className="text-danger">*</span> */}
                            </label>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <div>
                                        <CKEditor
                                            {...field}
                                            editor={ClassicEditor}
                                            data={field.value || ""}
                                            onChange={(event, editor) => {
                                                field.onChange(editor.getData());
                                            }}
                                            config={{
                                                toolbar: [
                                                    "heading",
                                                    "|",
                                                    "bold",
                                                    "italic",
                                                    "underline",
                                                    "strikethrough",
                                                    "|",
                                                    "fontColor",
                                                    "fontBackgroundColor",
                                                    "fontFamily",
                                                    "fontSize",
                                                    "alignment",
                                                    "|",
                                                    "link",
                                                    "bulletedList",
                                                    "numberedList",
                                                    "blockQuote",
                                                    "undo",
                                                    "redo",
                                                ],
                                                image: {
                                                    toolbar: [
                                                        "imageStyle:alignLeft",
                                                        "imageStyle:alignCenter",
                                                        "imageStyle:alignRight",
                                                        "|",
                                                        "imageTextAlternative",
                                                    ],
                                                },
                                                height: "500px", // custom height for CKEditor
                                            }}
                                        />
                                        {/* <ErrorMessage message={errors?.description?.message} /> */}
                                    </div>
                                )}
                            />
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div className="row">
                        <div className="col-12" />
                        <div className="col-12 col-md-6 col-lg-5">
                            <div className="form-group mb-3">
                                <label className="form-label" htmlFor="documentUpload">
                                    Uploads
                                </label>
                                <input
                                    type="file"
                                    className="form-control d-none"
                                    id="documentUpload"
                                    accept="image/*,application/pdf"
                                    onChange={handleFileChange}
                                    multiple
                                />
                                <label
                                    htmlFor="documentUpload"
                                    className="form-label rounded-4 cursor-pointer d-flex align-items-center justify-content-center"
                                    style={{
                                        display: "inline-block",
                                        cursor: "pointer",
                                        padding: 5,
                                        backgroundColor: "rgb(245, 245, 245)",
                                        borderRadius: 8,
                                        minWidth: 150,
                                    }}
                                >
                                    <div className="d-flex flex-column">
                                        <span className="placeholder-img">
                                            <UploadPlusIcon />
                                        </span>
                                        <span className="text-muted">Choose Files</span>
                                    </div>
                                </label>

                                {/* Preview Section */}
                                <div className="preview-section p-1 mt-3">
                                    {uploadedFiles.map((file) => (
                                        <div
                                            key={file.key}
                                            className="d-flex align-items-center justify-content-between p-3 border rounded mb-2"
                                            style={{ backgroundColor: "#f8f9fa" }}
                                        >
                                            <div
                                                className="w-100"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    setSelectedDocument(file.file);
                                                    toggleModal();
                                                }}
                                            >
                                                <span>{file.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="text-danger mx-2 bg-light border-0"
                                                onClick={() => handleRemoveFile(file.key)}
                                            >
                                                <DeleteIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="preview-section p-1 mt-3">
                                    {existingFiles.map((file) => (
                                        <div
                                            key={file.id}
                                            className="d-flex align-items-center justify-content-between p-3 border rounded mb-2"
                                            style={{ backgroundColor: "#f8f9fa" }}
                                        >
                                            <div
                                                className="w-100"
                                                style={{ cursor: "pointer" }}
                                                onClick={() => {
                                                    const docUrl = {
                                                        url: file?.url,
                                                    };
                                                    setSelectedDocument(docUrl);
                                                    toggleModal();
                                                }}
                                            >
                                                <span>{file?.name}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className="text-danger mx-2 bg-light border-0"
                                                onClick={() => handleDeleteDocument(file.id)}
                                            >
                                                <DeleteIcon />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary btn-lg">
                            Submit Request
                        </button>
                    </div>
                </form>
            </div>

            {/* Document Preview Modal */}
            <ViewDocumentModal
                modal={modal}
                toggle={toggleModal}
                selectedDocument={selectedDocument}
            />
        </>
    );
}
